import React from "react";
import CreateDeploymentForm from "../pages/application-summary-page/CreateDeploymentForm";
import UpdateDeploymentForm from "../pages/application-summary-page/UpdateDeploymentForm";
import { UpdateDeploymentProps } from "./DeploymentDetails";
import Dialog from "legacy-components/dialog/Dialog";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import type { RegionGroup } from "../config/deploymentRegions";

const useLegacyDialogStyles = makeStyles(() =>
  createStyles({
    dialogContent: {
      padding: "5px 2px 5px 2px",
    },
  })
);

const useDrawerStyles = makeStyles((theme) =>
  createStyles({
    drawerPaper: {
      width: 560,
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: "28px 28px 24px",
      top: 0,
      height: "100vh",
      boxShadow: "-3px 4px 2px 1px rgba(0, 0, 0, 0.25)",
      display: "flex",
      flexDirection: "column",
      background: "#FFFFFF",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 24,
    },
    title: {
      fontFamily: "'Open Sans', sans-serif",
      fontWeight: 600,
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: 0.15,
      color: "#252A2E",
    },
    closeButton: { padding: theme.spacing(0.5) },
    content: {
      flex: 1,
      overflowY: "auto",
      paddingRight: 8,
    },
  })
);

interface CreateDeploymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  basePath: string;
  resourceAppId: string;
  version: string;
  regionGroups?: RegionGroup[];
  availableRegions?: { key: string; value: string }[];
  /** Target regions already used for this version (add flow); those options are disabled in the drawer */
  deployedRegions?: string[];
  updateDeployment?: UpdateDeploymentProps;
}

const supportRegionalDeployments =
  process.env.REACT_APP_SUPPORT_REGIONAL_DEPLOYMENTS === "true";

export const CreateDeploymentDialog: React.FC<CreateDeploymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  basePath,
  resourceAppId,
  version,
  regionGroups = [],
  availableRegions = [],
  deployedRegions = [],
  updateDeployment,
}) => {
  const legacyClasses = useLegacyDialogStyles();
  const drawerClasses = useDrawerStyles();

  const regionalEditRegions = updateDeployment
    ? regionGroups.flatMap((g) => g.regions)
    : [];

  if (!supportRegionalDeployments) {
    const title = updateDeployment?.isUpdate
      ? "Edit Deployment"
      : "Add New Deployment";
    return (
      <Dialog open={open} title={title} onClose={onClose}>
        <Box className={legacyClasses.dialogContent}>
          {updateDeployment?.isUpdate ? (
            <UpdateDeploymentForm
              onCancel={onClose}
              onSuccess={onSuccess}
              basePath={basePath}
              version={version}
              availableRegions={availableRegions}
              updateDeploymentInitialValue={updateDeployment}
            />
          ) : (
            <CreateDeploymentForm
              onCancel={onClose}
              onSuccess={onSuccess}
              basePath={basePath}
              resourceAppId={resourceAppId}
              version={version}
              availableRegions={availableRegions}
              deployedRegions={deployedRegions}
            />
          )}
        </Box>
      </Dialog>
    );
  }

  const drawerTitle = updateDeployment?.isUpdate
    ? "Edit deployment"
    : "Add a new deployment";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      classes={{ paper: drawerClasses.drawerPaper }}
      ModalProps={{ hideBackdrop: false }}
      SlideProps={{ mountOnEnter: true, unmountOnExit: true }}
    >
      <Box className={drawerClasses.header}>
        <Typography className={drawerClasses.title}>{drawerTitle}</Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className={drawerClasses.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={drawerClasses.content}>
        {updateDeployment?.isUpdate ? (
          <UpdateDeploymentForm
            onCancel={onClose}
            onSuccess={onSuccess}
            basePath={basePath}
            version={version}
            availableRegions={regionalEditRegions}
            updateDeploymentInitialValue={updateDeployment}
          />
        ) : (
          <CreateDeploymentForm
            onCancel={onClose}
            onSuccess={onSuccess}
            basePath={basePath}
            resourceAppId={resourceAppId}
            version={version}
            regionGroups={regionGroups}
            deployedRegions={deployedRegions}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default CreateDeploymentDialog;
